import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Save } from 'lucide-react'
import toast from 'react-hot-toast'

import { PageHeader } from '@/components/shared/PageHeader'
import { showError, showSuccess } from '@/lib/swal'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { SearchableSelect, type SearchableSelectOption } from '@/components/ui/searchable-select'
import { useAuthStore } from '@/store/auth.store'
import { leaveRequestService } from '@/services/leaveRequestService'
import { userService } from '@/services/userService'
import {
  leavePolicyService,
  countWorkingLeaveDays,
  workingLeaveDaysOverlap,
  attendedWorkingDaysOverlap,
  type LeavePolicySettingDto,
} from '@/services/leavePolicyService'
import type {
  LeaveRequestSavePayload,
  LeaveRequestLookupItem,
  LeaveRequestEmployeeItem,
  EmployeeContractLeaveBalance,
  LeaveBalanceBucket,
  LeaveRequestConstraints,
} from '@/api/types'

function leaveRequestApiError(err: any, fallback: string): string {
  const data = err?.response?.data
  const fromResult =
    data?.result?.msg ??
    data?.Result?.msg ??
    data?.result?.Msg ??
    data?.Result?.Msg
  if (typeof fromResult === 'string' && fromResult.trim()) return fromResult.trim()
  if (typeof data?.message === 'string' && data.message.trim()) return data.message.trim()
  return err?.message ?? fallback
}

function leaveTypeNames(item: LeaveRequestLookupItem): string[] {
  return [item.name, item.nameEn, item.lookupNameEn, item.nameAr, item.lookupNameAr]
    .filter(Boolean)
    .map((s) => String(s).toLowerCase().replace(/\s+/g, ' ').trim())
}

function leaveBucketKey(typeName: string): keyof Pick<EmployeeContractLeaveBalance, 'annual' | 'sick' | 'casual'> {
  const n = (typeName || '').toLowerCase()
  if (n.includes('sick')) return 'sick'
  if (n.includes('casual')) return 'casual'
  return 'annual'
}

export const LeaveRequestDetail: React.FC = () => {
  const { t } = useTranslation('common')
  const navigate = useNavigate()
  const params = useParams<{ id: string }>()
  const { user } = useAuthStore()
  const branchId = user?.selectedBranch?.branchId ?? 0
  const userId = user?.userId ?? 0

  const isNew = params.id === 'new' || !params.id
  const editId = !isNew ? Number(params.id) : 0

  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [empId, setEmpId] = useState<number>(0)
  const [loggedInEmpId, setLoggedInEmpId] = useState<number>(0)
  const [leaveBalance, setLeaveBalance] = useState<EmployeeContractLeaveBalance | null>(null)
  const [constraints, setConstraints] = useState<LeaveRequestConstraints | null>(null)
  const canChangeEmployee = loggedInEmpId <= 0

  const [leaveTypes, setLeaveTypes] = useState<LeaveRequestLookupItem[]>([])
  const [employees, setEmployees] = useState<LeaveRequestEmployeeItem[]>([])
  const [leavePolicy, setLeavePolicy] = useState<LeavePolicySettingDto | null>(null)

  const [leaveTypeId, setLeaveTypeId] = useState<number>(0)
  const [substituteEmpId, setSubstituteEmpId] = useState<number>(0)
  const [fromDate, setFromDate] = useState<string>('')
  const [toDate, setToDate] = useState<string>('')
  const [details, setDetails] = useState('')
  const [status, setStatus] = useState<number>(0)
  const [approvalDate, setApprovalDate] = useState<string | null>(null)
  const [isEditable, setIsEditable] = useState<number>(0)

  const req = t('leaveRequestPage.requiredMark')

  const calcRequestedLeaveDays = (from: string, to: string) =>
    countWorkingLeaveDays(from, to, leavePolicy)

  const requestedDays = useMemo(
    () => calcRequestedLeaveDays(fromDate, toDate),
    [fromDate, toDate, leavePolicy]
  )

  const selectedLeaveTypeName = useMemo(() => {
    const selected = (leaveTypes || []).find((x) => x.id === leaveTypeId)
    if (!selected) return ''
    return selected.lookupNameEn ?? selected.nameEn ?? selected.name ?? ''
  }, [leaveTypes, leaveTypeId])

  const selectedBucket: LeaveBalanceBucket | undefined = useMemo(() => {
    if (!leaveBalance) return undefined
    const key = leaveBucketKey(selectedLeaveTypeName)
    return leaveBalance[key] ?? (key === 'annual'
      ? {
          entitlement: Number(leaveBalance.leaveBalance ?? 0) || 0,
          availed: Number(leaveBalance.requestedLeave ?? 0) || 0,
          balance: Number(leaveBalance.remainingLeave ?? 0) || 0,
        }
      : undefined)
  }, [leaveBalance, selectedLeaveTypeName])

  const displayedLeaveBalance = Number(selectedBucket?.entitlement ?? 0) || 0
  const remainingBeforeRequest = Number(selectedBucket?.balance ?? 0) || 0
  const displayedRemainingLeave = Math.max(0, remainingBeforeRequest - (requestedDays || 0))

  useEffect(() => {
    if (!branchId || !userId) return
    const load = async () => {
      try {
        const u = await userService.getUserById(branchId, userId) as any
        const eId = Number(u?.empId ?? u?.employeeId ?? u?.EmpId ?? u?.EmployeeId ?? 0) || 0
        setLoggedInEmpId(eId)
        if (eId > 0) {
          setEmpId((prev) => (prev > 0 ? prev : eId))
        }
      } catch (e: any) {
        toast.error(e?.response?.data?.message ?? e?.message ?? t('leaveRequestPage.loadEmployeeInfoFailed'))
      }
    }
    load()
  }, [branchId, userId, t])

  useEffect(() => {
    if (!branchId || !empId) {
      setLeaveBalance(null)
      setConstraints(null)
      return
    }
    let cancelled = false
    const load = async () => {
      try {
        const [contract, leaveConstraints] = await Promise.all([
          leaveRequestService.getEmployeeContract(empId, branchId),
          leaveRequestService.getConstraints(empId, branchId),
        ])
        if (!cancelled) {
          setLeaveBalance(contract ?? null)
          setConstraints(leaveConstraints ?? null)
        }
      } catch (e: any) {
        if (!cancelled) {
          setLeaveBalance(null)
          setConstraints(null)
          toast.error(e?.response?.data?.message ?? e?.message ?? t('leaveRequestPage.loadEmployeeInfoFailed'))
        }
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [branchId, empId, t])

  useEffect(() => {
    if (!branchId) return
    const loadLookups = async () => {
      try {
        const [types, emps, policy] = await Promise.all([
          leaveRequestService.getLeaveTypes(),
          leaveRequestService.getActiveEmployees(branchId),
          leavePolicyService.getByBranchId(branchId),
        ])
        setLeaveTypes(types || [])
        setEmployees(emps || [])
        setLeavePolicy(policy)
      } catch (e: any) {
        toast.error(e?.response?.data?.message ?? e?.message ?? t('leaveRequestPage.loadFormFailed'))
      }
    }
    loadLookups()
  }, [branchId])

  useEffect(() => {
    if (!branchId || isNew || !editId) return
    const load = async () => {
      try {
        setLoading(true)
        const vm = await leaveRequestService.getById(editId, branchId)
        if (!vm) {
          navigate('/self-services/leave-requests')
          return
        }
        setEmpId(vm.empId ?? 0)
        setLeaveTypeId(vm.leaveTypeId ?? 0)
        setSubstituteEmpId(vm.substituteEmpId ?? 0)
        setFromDate(vm.fromDate ? String(vm.fromDate).slice(0, 10) : '')
        setToDate(vm.toDate ? String(vm.toDate).slice(0, 10) : '')
        setDetails(vm.details ?? '')
        setStatus(vm.status ?? 0)
        setApprovalDate(vm.approvalDate ?? null)
        setIsEditable(vm.isEditable ?? 0)
      } catch (e: any) {
        toast.error(e?.response?.data?.message ?? e?.message ?? t('leaveRequestPage.loadFailed'))
        navigate('/self-services/leave-requests')
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [branchId, isNew, editId, navigate])

  const leaveTypeOptions = useMemo((): SearchableSelectOption[] => {
    const allowed = new Set(['annual leave', 'sick leave', 'casual leave'])
    const filtered = (leaveTypes || []).filter((x) =>
      leaveTypeNames(x).some((n) => allowed.has(n))
    )
    return [
      { value: '0', label: t('leaveRequestPage.placeholderSelectLeaveType') },
      ...filtered.filter((x) => x.id).map((x) => ({
        value: String(x.id),
        label: x.name ?? x.nameEn ?? x.lookupNameEn ?? t('leaveRequestPage.typeFallback', { id: x.id }),
      })),
    ]
  }, [leaveTypes, t])

  const employeeOptions = useMemo((): SearchableSelectOption[] => {
    const opts = employees
      .filter((e) => e.id)
      .map((e) => ({
        value: String(e.empId ?? e.id),
        label: e.fullName ?? t('leaveRequestPage.employeeFallback', { id: e.id }),
      }))
    if (empId > 0 && !opts.some((o) => o.value === String(empId))) {
      opts.unshift({
        value: String(empId),
        label: t('leaveRequestPage.employeeFallback', { id: empId }),
      })
    }
    return [{ value: '0', label: t('leaveRequestPage.placeholderSelectEmployee') }, ...opts]
  }, [employees, empId, t])

  const substituteOptions = useMemo((): SearchableSelectOption[] => {
    return [
      { value: '0', label: t('leaveRequestPage.placeholderSelectEmployee') },
      ...employees
        .filter((e) => e.id && (e.empId ?? e.id) !== empId)
        .map((e) => ({
          value: String(e.empId ?? e.id),
          label: e.fullName ?? t('leaveRequestPage.employeeFallback', { id: e.id }),
        })),
    ]
  }, [employees, empId, t])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!branchId) return
    if (!empId) {
      await showError(t('leaveRequestPage.saveFailedTitle'), t('leaveRequestPage.toastEmployeeLink'))
      return
    }
    if (!leaveTypeId) {
      await showError(t('leaveRequestPage.saveFailedTitle'), t('leaveRequestPage.toastLeaveType'))
      return
    }
    if (!substituteEmpId) {
      await showError(t('leaveRequestPage.saveFailedTitle'), t('leaveRequestPage.toastSubstitute'))
      return
    }
    if (!fromDate) {
      await showError(t('leaveRequestPage.saveFailedTitle'), t('leaveRequestPage.toastFromDate'))
      return
    }
    if (!toDate) {
      await showError(t('leaveRequestPage.saveFailedTitle'), t('leaveRequestPage.toastToDate'))
      return
    }
    if (!details.trim()) {
      await showError(t('leaveRequestPage.saveFailedTitle'), t('leaveRequestPage.toastDetails'))
      return
    }

    if (!leaveBalance) {
      await showError(t('leaveRequestPage.saveFailedTitle'), t('leaveRequestPage.toastLeaveBalanceUnavailable'))
      return
    }
    if (isNew && constraints?.hasPending) {
      await showError(t('leaveRequestPage.saveFailedTitle'), t('leaveRequestPage.toastPendingExists'))
      return
    }
    if (
      (constraints?.granted || []).some((g) =>
        workingLeaveDaysOverlap(fromDate, toDate, g.dateFrom, g.dateTo, leavePolicy)
      )
    ) {
      await showError(t('leaveRequestPage.saveFailedTitle'), t('leaveRequestPage.toastDaysAlreadyGranted'))
      return
    }
    if (attendedWorkingDaysOverlap(fromDate, toDate, constraints?.attendedDates, leavePolicy)) {
      await showError(t('leaveRequestPage.saveFailedTitle'), t('leaveRequestPage.toastAttendanceMarked'))
      return
    }

    const requestedDays = calcRequestedLeaveDays(fromDate, toDate)
    const remainingLeave = remainingBeforeRequest
    if (fromDate && toDate && new Date(toDate + 'T00:00:00') < new Date(fromDate + 'T00:00:00')) {
      await showError(t('leaveRequestPage.saveFailedTitle'), t('leaveRequestPage.toastInvalidDateRange'))
      return
    }
    if (requestedDays <= 0) {
      await showError(t('leaveRequestPage.saveFailedTitle'), t('leaveRequestPage.toastNoWorkingDays'))
      return
    }
    if (remainingLeave <= 0) {
      await showError(t('leaveRequestPage.saveFailedTitle'), t('leaveRequestPage.toastZeroRemaining'))
      return
    }
    if (requestedDays > remainingLeave) {
      await showError(
        t('leaveRequestPage.saveFailedTitle'),
        t('leaveRequestPage.toastRequestedExceedsRemaining', { requested: requestedDays, remaining: remainingLeave })
      )
      return
    }

    const dateFromIso = fromDate ? new Date(fromDate + 'T00:00:00.000Z').toISOString() : ''
    const dateToIso = toDate ? new Date(toDate + 'T00:00:00.000Z').toISOString() : ''

    const payload: LeaveRequestSavePayload = {
      id: isNew ? 0 : editId,
      empId,
      leaveTypeId,
      dateFrom: dateFromIso,
      dateTo: dateToIso,
      substituteId: substituteEmpId,
      reason: details.trim(),
      status,
      approvalDate,
      branchId,
      isEditable,
    }

    try {
      setSaving(true)
      if (isNew) {
        await leaveRequestService.create(payload)
        await showSuccess(t('leaveRequestPage.savedTitle'), t('leaveRequestPage.savedMessage'), 3000)
      } else {
        await leaveRequestService.update(payload)
        await showSuccess(t('leaveRequestPage.updatedTitle'), t('leaveRequestPage.updatedMessage'), 3000)
      }
      navigate('/self-services/leave-requests')
    } catch (err: any) {
      const msg = leaveRequestApiError(err, t('leaveRequestPage.saveFailed'))
      await showError(isNew ? t('leaveRequestPage.saveFailedTitle') : t('leaveRequestPage.updateFailedTitle'), msg, 5000)
    } finally {
      setSaving(false)
    }
  }

  if (!branchId) {
    return (
      <div className="space-y-6 p-6">
        <PageHeader title={t('leaveRequestPage.pageTitle')} description={t('leaveRequestPage.pageDescription')} />
        <Card>
          <CardContent className="p-6">
            <p className="text-muted-foreground">{t('leaveRequestPage.selectBranchFirst')}</p>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6 p-6">
      <PageHeader title={t('leaveRequestPage.pageTitle')} description={t('leaveRequestPage.pageDescription')} />

      <form onSubmit={handleSubmit}>
        <Card>
          <CardContent className="p-6 space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 rounded-md border border-input bg-muted/30 p-4">
              <div>
                <p className="text-sm text-muted-foreground">{t('leaveRequestPage.statLeaveBalance')}</p>
                <p className="text-lg font-semibold">{displayedLeaveBalance}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t('leaveRequestPage.statRequestedLeave')}</p>
                <p className="text-lg font-semibold">{requestedDays || 0}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t('leaveRequestPage.statRemainingLeave')}</p>
                <p className="text-lg font-semibold">{displayedRemainingLeave}</p>
              </div>
              <p className="sm:col-span-3 text-xs text-muted-foreground">{t('leaveRequestPage.hintOffDaysExcluded')}</p>
            </div>
            {isNew && constraints?.hasPending && (
              <p className="text-sm text-destructive">{t('leaveRequestPage.bannerPendingExists')}</p>
            )}
            {attendedWorkingDaysOverlap(fromDate, toDate, constraints?.attendedDates, leavePolicy) && (
              <p className="text-sm text-destructive">{t('leaveRequestPage.bannerAttendanceMarked')}</p>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <SearchableSelect
                  label={`${req} ${t('leaveRequestPage.labelEmployee')}`}
                  options={employeeOptions}
                  value={empId ? String(empId) : '0'}
                  onValueChange={(v) => {
                    if (!canChangeEmployee) return
                    setEmpId(Number(v) || 0)
                  }}
                  placeholder={t('leaveRequestPage.placeholderSelectEmployee')}
                  searchPlaceholder={t('leaveRequestPage.searchEmployee')}
                  emptyMessage={t('leaveRequestPage.emptyEmployee')}
                  disabled={!canChangeEmployee}
                />
              </div>
              <div className="space-y-2">
                <SearchableSelect
                  label={`${req} ${t('leaveRequestPage.labelLeaveType')}`}
                  options={leaveTypeOptions}
                  value={leaveTypeId ? String(leaveTypeId) : '0'}
                  onValueChange={(v) => setLeaveTypeId(Number(v) || 0)}
                  placeholder={t('leaveRequestPage.placeholderSelectLeaveType')}
                  searchPlaceholder={t('leaveRequestPage.searchShort')}
                  emptyMessage={t('leaveRequestPage.emptyLeaveType')}
                />
              </div>
              <div className="space-y-2">
                <SearchableSelect
                  label={`${req} ${t('leaveRequestPage.labelSubstitute')}`}
                  options={substituteOptions}
                  value={substituteEmpId ? String(substituteEmpId) : '0'}
                  onValueChange={(v) => setSubstituteEmpId(Number(v) || 0)}
                  placeholder={t('leaveRequestPage.placeholderSelectEmployee')}
                  searchPlaceholder={t('leaveRequestPage.searchEmployee')}
                  emptyMessage={t('leaveRequestPage.emptyEmployee')}
                />
              </div>
              <div className="space-y-2">
                <Label>{req} {t('leaveRequestPage.labelFrom')}</Label>
                <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label>{req} {t('leaveRequestPage.labelTo')}</Label>
                <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label>{req} {t('leaveRequestPage.labelDetails')}</Label>
                <Textarea value={details} onChange={(e) => setDetails(e.target.value)} placeholder={t('leaveRequestPage.placeholderDetails')} rows={4} />
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" disabled={saving || loading} onClick={() => navigate('/self-services/leave-requests')}>
                {t('leaveRequestPage.return')}
              </Button>
              <Button type="submit" disabled={saving || loading || (isNew && !!constraints?.hasPending) || attendedWorkingDaysOverlap(fromDate, toDate, constraints?.attendedDates, leavePolicy)}>
                <Save className="h-4 w-4 mr-2" />
                {saving ? t('leaveRequestPage.saving') : isNew ? t('leaveRequestPage.save') : t('leaveRequestPage.update')}
              </Button>
            </div>
          </CardContent>
        </Card>
      </form>
    </div>
  )
}
